/**
 * SoleadLab 2.0 — Create IV Session Modal (shared)
 *
 * File-drop-first flow:
 *   1. Drop zone — drop one or more measurement files
 *   2. Review   — auto-filled fields + grouped sheet checklist per file
 *
 * Multiple files of the same type (e.g., min/mid/full from several days)
 * can be imported into a single session together.
 */

import { useRef, useState } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import type { CreateSessionInput } from '@/types/iv';
import { parseFile, type ParsedSheet } from './ivParser';
import { detectAdapter, type IVDeviceAdapter } from './ivAdapters';

// ─── Date extraction ──────────────────────────────────────────────────────────

function parseDateOnly(val: unknown): string | null {
  if (val == null || val === '') return null;
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  const str = String(val).trim();
  if (!str) return null;
  if (/^\d{5}$/.test(str)) {
    const d = new Date(Math.round((Number(str) - 25569) * 86400 * 1000));
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  const datePart = str.split(/[\sT]/)[0];
  return datePart.length >= 6 ? datePart : null;
}

function extractInspectionDates(adapter: IVDeviceAdapter | null, rawRows: unknown[][]): string[] {
  const labels: string[] = adapter?.labelMapping?.extrasLabels?.timestamp as string[] ?? ['Date and time'];
  const found = new Set<string>();
  for (const row of rawRows) {
    if (!Array.isArray(row)) continue;
    for (let c = 0; c < row.length - 1; c++) {
      const cell = String(row[c] ?? '').trim();
      if (labels.some((l) => cell.startsWith(l))) {
        const dateStr = parseDateOnly(row[c + 1]);
        if (dateStr) found.add(dateStr);
      }
    }
  }
  return Array.from(found).sort();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectScopeFromRawRows(rawRows: unknown[][]): 'module' | 'string' {
  for (let r = 0; r < Math.min(5, rawRows.length); r++) {
    const row = Array.isArray(rawRows[r]) ? rawRows[r] as unknown[] : [];
    for (let c = 0; c < row.length - 1; c++) {
      if (String(row[c] ?? '').trim() === 'Number of modules') {
        const val = Number(row[c + 1]);
        if (!isNaN(val)) return val > 1 ? 'string' : 'module';
      }
    }
  }
  return 'module';
}

function fileNameWithoutExt(name: string): string {
  const lastDot = name.lastIndexOf('.');
  return lastDot > 0 ? name.slice(0, lastDot) : name;
}

function estimateCount(
  rawRows: unknown[][],
  adapter: IVDeviceAdapter | null,
  blockSize: number,
  firstRow: number,
): number {
  if (adapter?.estimateCount) return adapter.estimateCount(rawRows);
  const available = rawRows.length - firstRow;
  return available > 0 ? Math.floor(available / blockSize) : 0;
}

// ─── Internal types ───────────────────────────────────────────────────────────

interface ParsedSheetEntry {
  fileIdx: number;       // which file this sheet belongs to
  fileName: string;      // display name
  sheet: ParsedSheet;
  isFiltered: boolean;   // false = different structure (e.g. "samples")
  isRecommended: boolean; // pre-selected by default
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SheetImportTarget {
  name: string;
  rawRows: unknown[][];
}

export interface CreateIVSessionModalProps {
  onClose: () => void;
  onCreate: (
    input: CreateSessionInput,
    file: File,
    adapter: IVDeviceAdapter | null,
    sheets: SheetImportTarget[],
  ) => Promise<void>;
}

// ─── Component ────────────────────────────────────────────────────────────────

type ModalStep = 'drop' | 'review';

export default function CreateIVSessionModal({ onClose, onCreate }: CreateIVSessionModalProps) {
  const projectSiteName = useProjectStore((s) => s.meta.siteName);

  const [step, setStep] = useState<ModalStep>('drop');
  const [dragOver, setDragOver] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');

  // All files and their parsed sheets
  const [files, setFiles] = useState<File[]>([]);
  const [entries, setEntries] = useState<ParsedSheetEntry[]>([]);
  const [selectedIdxs, setSelectedIdxs] = useState<Set<number>>(new Set());

  // Detected adapter (from first file, applied to all)
  const [detectedAdapter, setDetectedAdapter] = useState<IVDeviceAdapter | null>(null);
  const [inspectionDates, setInspectionDates] = useState<string[]>([]);

  // Review form
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'module' | 'string'>('module');
  const [siteName, setSiteName] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const addMoreRef = useRef<HTMLInputElement>(null);

  // ── Parse and append files ─────────────────────────────────────────────────
  const processFiles = async (newFiles: File[], isFirstBatch: boolean) => {
    if (newFiles.length === 0) return;
    setParsing(true);
    setParseError('');

    try {
      const newEntries: ParsedSheetEntry[] = [];
      let adapterRef: IVDeviceAdapter | null = isFirstBatch ? null : detectedAdapter;

      for (let fi = 0; fi < newFiles.length; fi++) {
        const f = newFiles[fi];
        const result = await parseFile(f);
        if (!result.sheets.length) continue;

        // Detect adapter from first file of the first batch only
        if (!adapterRef) {
          adapterRef = detectAdapter(result.sheets[0].rawRows, f.name);
        }

        const fileIdx = (isFirstBatch ? 0 : files.length) + fi;
        for (const sheet of result.sheets) {
          const isFiltered = adapterRef?.filterSheet
            ? adapterRef.filterSheet(sheet.rawRows, sheet.name)
            : true;
          // Use recommendSheet for pre-selection if defined; fallback to filterSheet
          const isRecommended = adapterRef?.recommendSheet
            ? adapterRef.recommendSheet(sheet.name)
            : isFiltered;
          newEntries.push({ fileIdx, fileName: f.name, sheet, isFiltered, isRecommended });
        }
      }

      if (isFirstBatch) {
        // First batch: init all state
        setFiles(newFiles);
        setEntries(newEntries);
        setDetectedAdapter(adapterRef);

        const matchingIdxs = newEntries
          .map((e, i) => e.isRecommended ? i : -1)
          .filter((i) => i >= 0);
        setSelectedIdxs(new Set(matchingIdxs));

        // Session name from first file
        setName(fileNameWithoutExt(newFiles[0].name));
        setSiteName(projectSiteName ?? '');

        // Scope from first matching sheet
        if (matchingIdxs.length > 0) {
          setScope(detectScopeFromRawRows(newEntries[matchingIdxs[0]].sheet.rawRows));
        }

        // Inspection dates from all matching sheets
        const allRows = matchingIdxs.flatMap((i) => newEntries[i].sheet.rawRows);
        setInspectionDates(extractInspectionDates(adapterRef, allRows));

        setStep('review');
      } else {
        // Additional batch: merge into existing
        setFiles((prev) => [...prev, ...newFiles]);
        setEntries((prev) => {
          const merged = [...prev, ...newEntries];
          return merged;
        });
        setSelectedIdxs((prev) => {
          const next = new Set(prev);
          const offset = entries.length; // current length before append
          newEntries.forEach((e, i) => {
            if (e.isRecommended) next.add(offset + i);
          });
          return next;
        });

        // Extend inspection dates from new matching rows
        const newMatchingRows = newEntries
          .filter((e) => e.isFiltered)
          .flatMap((e) => e.sheet.rawRows);
        const newDates = extractInspectionDates(adapterRef, newMatchingRows);
        setInspectionDates((prev) => {
          const merged = new Set([...prev, ...newDates]);
          return Array.from(merged).sort();
        });
      }
    } catch (err) {
      setParseError(String(err));
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      /\.(xlsx|xls|csv)$/i.test(f.name)
    );
    if (dropped.length > 0) await processFiles(dropped, step === 'drop');
  };

  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) await processFiles(selected, step === 'drop');
    e.target.value = ''; // reset so same file can be added again
  };

  const handleAddMoreFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) await processFiles(selected, false);
    e.target.value = '';
  };

  const toggleIdx = (idx: number, checked: boolean) => {
    setSelectedIdxs((prev) => {
      const next = new Set(prev);
      if (checked) next.add(idx);
      else next.delete(idx);
      return next;
    });
  };

  const handleCreate = async () => {
    if (!name.trim() || files.length === 0) return;
    setSaving(true);
    setSaveError('');
    const selectedSheets: SheetImportTarget[] = Array.from(selectedIdxs)
      .sort((a, b) => a - b)
      .map((i) => ({ name: entries[i].sheet.name, rawRows: entries[i].sheet.rawRows }));
    try {
      await onCreate(
        {
          name: name.trim(),
          measurement_scope: scope,
          site_name: siteName.trim() || undefined,
          notes: notes.trim() || undefined,
          inspection_date: inspectionDates.length > 0 ? inspectionDates.join(', ') : undefined,
        },
        files[0], // source file = first file
        detectedAdapter,
        selectedSheets,
      );
    } catch (err) {
      setSaveError(String(err));
      setSaving(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const blockSize = detectedAdapter?.blockConfig?.blockSize ?? 16;
  const firstRow = detectedAdapter?.blockConfig?.firstRow ?? 0;
  const totalEstimated = Array.from(selectedIdxs).reduce(
    (sum, i) => sum + estimateCount(entries[i]?.sheet.rawRows ?? [], detectedAdapter, blockSize, firstRow),
    0,
  );

  // Hide the sheet checklist when every valid (filtered) sheet is already
  // recommended — there's no meaningful choice for the user to make.
  const hasChoices = entries.some((e) => e.isFiltered && !e.isRecommended);

  // Group entries by fileIdx for display
  const fileGroups: Array<{ fileName: string; fileIdx: number; entries: Array<{ idx: number; entry: ParsedSheetEntry }> }> = [];
  entries.forEach((entry, idx) => {
    let group = fileGroups.find((g) => g.fileIdx === entry.fileIdx);
    if (!group) {
      group = { fileName: entry.fileName, fileIdx: entry.fileIdx, entries: [] };
      fileGroups.push(group);
    }
    group.entries.push({ idx, entry });
  });

  const hasAnySheets = entries.length > 0;

  return (
    <div
      className="iv-modal-backdrop"
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="iv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="iv-modal__title">New IV Session</div>

        {/* ── Step 1: Drop ─────────────────────────────────────────────────── */}
        {step === 'drop' && (
          <>
            <div
              className={`iv-drop-zone${dragOver ? ' iv-drop-zone--active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {parsing ? (
                <span className="iv-drop-zone__text">Parsing files…</span>
              ) : (
                <>
                  <span className="iv-drop-zone__icon">📂</span>
                  <span className="iv-drop-zone__text">Drop measurement file(s) here</span>
                  <span className="iv-drop-zone__sub">or click to browse — .xlsx, .xls, .csv · multiple files supported</span>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
            {parseError && <div className="iv-note iv-note--error">{parseError}</div>}
            <div className="iv-modal__actions">
              <button className="iv-btn" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}

        {/* ── Step 2: Review ───────────────────────────────────────────────── */}
        {step === 'review' && hasAnySheets && (
          <>
            {detectedAdapter && (
              <div className="iv-note iv-note--info" style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span>
                    <strong>{detectedAdapter.icon} {detectedAdapter.name}</strong> detected
                    {totalEstimated > 0 && ` — ${totalEstimated} measurements`}
                    {files.length > 1 && (
                      <span style={{ marginLeft: 4, opacity: 0.8 }}>
                        from {files.length} files
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="iv-btn iv-btn--sm"
                    disabled={parsing}
                    onClick={() => addMoreRef.current?.click()}
                    style={{ flexShrink: 0, fontSize: 'var(--sl-text-xs)' }}
                  >
                    {parsing ? 'Parsing…' : '＋ Add file'}
                  </button>
                </div>
                {inspectionDates.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 'var(--sl-text-xs)', opacity: 0.85 }}>
                    📅 {inspectionDates.length > 1 ? 'Dates' : 'Date'}: <strong>{inspectionDates.join(', ')}</strong>
                  </div>
                )}
              </div>
            )}
            {!detectedAdapter && (
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="iv-btn iv-btn--sm"
                  disabled={parsing}
                  onClick={() => addMoreRef.current?.click()}
                >
                  {parsing ? 'Parsing…' : '＋ Add more files'}
                </button>
              </div>
            )}
            <input
              ref={addMoreRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              multiple
              style={{ display: 'none' }}
              onChange={handleAddMoreFiles}
            />

            {saveError && <div className="iv-note iv-note--error">{saveError}</div>}
            {parseError && <div className="iv-note iv-note--error">{parseError}</div>}

            {/* Sheet checklist — only shown when there are meaningful choices */}
            {hasChoices && (
              <div className="iv-form-group">
                <label>Sheets to import</label>
                <div className="iv-sheet-checklist">
                  {fileGroups.map((group) => (
                    <div key={group.fileIdx} className="iv-sheet-file-group">
                      {fileGroups.length > 1 && (
                        <div className="iv-sheet-file-label">📄 {group.fileName}</div>
                      )}
                      {group.entries.map(({ idx, entry }) => {
                        const est = estimateCount(entry.sheet.rawRows, detectedAdapter, blockSize, firstRow);
                        return (
                          <label
                            key={idx}
                            className={`iv-sheet-check${!entry.isFiltered ? ' iv-sheet-check--dim' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIdxs.has(idx)}
                              onChange={(e) => toggleIdx(idx, e.target.checked)}
                            />
                            <span className="iv-sheet-check__name">{entry.sheet.name}</span>
                            {est > 0 && (
                              <span className="iv-sheet-check__count">~{est}</span>
                            )}
                            {!entry.isFiltered && (
                              <span className="iv-sheet-check__note">different structure</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="iv-form-group">
              <label>Name *</label>
              <input
                className="iv-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="iv-form-group">
              <label>Measurement Scope</label>
              <select
                className="iv-select"
                value={scope}
                onChange={(e) => setScope(e.target.value as 'module' | 'string')}
              >
                <option value="module">Module</option>
                <option value="string">String</option>
              </select>
            </div>

            <div className="iv-form-group">
              <label>Site Name</label>
              <input
                className="iv-input"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
              />
            </div>

            <div className="iv-form-group">
              <label>Notes</label>
              <textarea
                className="iv-input"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="iv-modal__actions">
              <button className="iv-btn" onClick={() => setStep('drop')} disabled={saving}>
                ← Back
              </button>
              <button
                className="iv-btn iv-btn--primary"
                onClick={handleCreate}
                disabled={saving || !name.trim() || selectedIdxs.size === 0}
              >
                {saving ? 'Creating…' : 'Create Session'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
